import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Pencil, Plus } from 'lucide-react'
import Card from '@/components/Card'
import { usePromotionRules, useUpdateRule, type PromotionRule } from '@/api/promotions'
import { IS_DEMO_MODE } from '@/lib/demoMode'
import { formatRuleConditions, formatCooldownHours } from '@/lib/ruleFormat'
import { cn } from '@/lib/utils'
import RuleDetailDrawer from './RuleDetailDrawer'

function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-[18px] w-8 items-center rounded-full transition-colors',
        checked ? 'bg-emerald-500' : 'bg-[var(--border)]',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[14px]' : 'translate-x-0.5',
        )}
      />
    </button>
  )
}

export default function RulesView() {
  const { data, isLoading } = usePromotionRules()
  const update = useUpdateRule()
  const qc = useQueryClient()

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerRule, setDrawerRule] = useState<PromotionRule | null>(null)

  const rules = data?.rules ?? []

  function openCreateDrawer() {
    setDrawerRule(null)
    setDrawerOpen(true)
  }

  function openEditDrawer(rule: PromotionRule) {
    setDrawerRule(rule)
    setDrawerOpen(true)
  }

  function closeDrawer() {
    setDrawerOpen(false)
    setDrawerRule(null)
  }

  function toggleRule(rule: PromotionRule, nextActive: boolean) {
    const optimisticActive = nextActive ? 1 : 0
    // Optimistic Update — rollback on error via invalidate
    qc.setQueryData<{ rules: PromotionRule[] }>(['promotionRules'], (old) =>
      old
        ? {
            rules: old.rules.map((r) =>
              r.id === rule.id ? { ...r, isActive: optimisticActive } : r,
            ),
          }
        : old,
    )
    update.mutate(
      { id: rule.id, isActive: optimisticActive },
      {
        onError: () => {
          qc.invalidateQueries({ queryKey: ['promotionRules'] })
        },
      },
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight text-[var(--foreground)]">
            Regelwerk
          </h1>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {rules.length} produktive Promotion-Rules · Cooldown pro Regel · Cron 08:00
            {IS_DEMO_MODE && (
              <span className="ml-2 font-semibold text-amber-600">(Cron im Demo-Modus pausiert)</span>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateDrawer}
          className="inline-flex items-center gap-1.5 rounded-md bg-[var(--foreground)] px-3 py-1.5 text-[12px] font-semibold text-[var(--background)] hover:opacity-90"
        >
          <Plus className="h-3.5 w-3.5" />
          Neue Regel
        </button>
      </div>

      {IS_DEMO_MODE && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          <strong className="font-bold">Demo-Modus:</strong> Cron pausiert — Rule-Aenderungen werden
          gespeichert, aber erst bei <code className="rounded bg-white/70 px-1">READ_ONLY_MODE=false</code>{' '}
          fuehrt der Auto-Apply-Worker sie an Amazon aus. Bearbeiten/Anlegen/Loeschen ist erlaubt.
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
            Lade Regeln…
          </div>
        ) : rules.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-[var(--muted-foreground)]">
            Keine Regeln konfiguriert.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]/40 text-left">
                  {['Regel', 'Flow', 'Bedingung', 'Aktiv', 'Cooldown', 'Max/Tag', ''].map(
                    (h, i) => (
                      <th
                        key={h || i}
                        className={cn(
                          'px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[var(--muted-foreground)]',
                          i >= 4 && i <= 5 && 'text-right',
                        )}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => {
                  const active = rule.isActive === 1
                  return (
                    <tr key={rule.id} className="border-b border-[var(--border)] last:border-b-0">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-[var(--foreground)]">{rule.name}</div>
                        {rule.description && (
                          <div className="text-[11.5px] text-[var(--muted-foreground)]">
                            {rule.description}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-1.5 font-mono text-[11.5px]">
                          <span className="text-[var(--muted-foreground)]">{rule.fromStage}</span>
                          <ArrowRight className="h-3 w-3 text-[var(--muted-foreground)]" />
                          <span className="font-bold text-[var(--foreground)]">{rule.toStage}</span>
                        </span>
                      </td>
                      <td className="px-3 py-3 font-mono text-[12px] text-[var(--foreground)]">
                        {formatRuleConditions(rule.conditions)}
                      </td>
                      <td className="px-3 py-3">
                        <ToggleSwitch
                          checked={active}
                          disabled={update.isPending}
                          onChange={(next) => toggleRule(rule, next)}
                        />
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-[var(--muted-foreground)]">
                        {formatCooldownHours(rule.cooldownMinutes)}
                      </td>
                      <td className="px-3 py-3 text-right font-mono tabular-nums text-[var(--foreground)]">
                        {rule.maxPerDay}
                      </td>
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          onClick={() => openEditDrawer(rule)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--border)] text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                          title="Bearbeiten"
                          aria-label={`Regel ${rule.name} bearbeiten`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RuleDetailDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        rule={drawerRule}
      />
    </div>
  )
}
